import { selectSplitShipAccessible, resetSplitShip } from '../redux/splitShip';
import { createLoadOrder } from '../actions/order';
import { getOrderByNumber } from '../selectors';
import Status from '../constants/Status';
import {
  ADD_ITEM_TAX_AMOUNT_SUCCESS,
  UPDATE_ITEM_BREAKDOWN_SUCCESS,
  DELETE_ITEM_BREAKDOWN_SUCCESS,
  UPDATE_ITEM_TAX_AMOUNT_SUCCESS,
  COPY_ITEM_SUCCESS,
  ADD_BREAKDOWN_COLOR_SUCCESS,
  ADD_BREAKDOWN_SIZE_SUCCESS,
  ADD_ITEM_COLOR_SUCCESS,
  ADD_ITEM_SIZE_SUCCESS,
  ADD_ITEM_SKU_SUCCESS,
  DELETE_ITEM_COLOR_SUCCESS,
  DELETE_ITEM_SIZE_SUCCESS,
  DELETE_ITEM_SKU_SUCCESS,
  ADD_ITEM_RETAIL_ADJUSTMENT_SUCCESS,
  UPDATE_ITEM_RETAIL_ADJUSTMENT_SUCCESS,
  UPDATE_ITEM_RETAIL_ADJUSTMENT_WITH_METADATA_SUCCESS,
  DELETE_ITEM_RETAIL_ADJUSTMENT_SUCCESS,
  ADD_VARIANT_SUCCESS,
  DELETE_VARIANT_SUCCESS,
  UPDATE_VARIANT_SUCCESS,
  ADD_ITEM_SUCCESS,
  ADD_REDIRECT_ITEM_SUCCESS,
  UPDATE_ITEM_SUCCESS,
  SIMPLE_UPDATE_ITEM_SUCCESS,
  COMPLEX_UPDATE_ITEM_SUCCESS,
  COMPLEX_UPDATE_BREAKDOWN_SUCCESS,
  COMPLEX_UPDATE_ITEM_COST_SUCCESS,
  DELETE_ITEM_SUCCESS,
  ADD_ITEM_COST_SUCCESS,
  UPDATE_ITEM_COST_SUCCESS,
  DELETE_ITEM_COST_SUCCESS,
  ADD_ITEM_BREAKDOWN_SUCCESS,
  DELETE_ITEM_TAX_AMOUNT_SUCCESS,
} from '../actions/item';
import { UPDATE_ORDER_TAX_ID_SUCESS } from '../actions/order';
import { SPLIT_SHIP_RESET } from '../redux/splitShip';

const splitShipReset = store => {
  let prevBreakdowns = {};
  let prevItems = {};

  return next => action => {
    const whiteListActions = [
      ADD_ITEM_TAX_AMOUNT_SUCCESS,
      UPDATE_ITEM_BREAKDOWN_SUCCESS,
      DELETE_ITEM_BREAKDOWN_SUCCESS,
      UPDATE_ITEM_TAX_AMOUNT_SUCCESS,
      COPY_ITEM_SUCCESS,
      ADD_BREAKDOWN_COLOR_SUCCESS,
      ADD_BREAKDOWN_SIZE_SUCCESS,
      ADD_ITEM_COLOR_SUCCESS,
      ADD_ITEM_SIZE_SUCCESS,
      ADD_ITEM_SKU_SUCCESS,
      DELETE_ITEM_COLOR_SUCCESS,
      DELETE_ITEM_SIZE_SUCCESS,
      DELETE_ITEM_SKU_SUCCESS,
      ADD_ITEM_RETAIL_ADJUSTMENT_SUCCESS,
      UPDATE_ITEM_RETAIL_ADJUSTMENT_SUCCESS,
      UPDATE_ITEM_RETAIL_ADJUSTMENT_WITH_METADATA_SUCCESS,
      DELETE_ITEM_RETAIL_ADJUSTMENT_SUCCESS,
      ADD_VARIANT_SUCCESS,
      DELETE_VARIANT_SUCCESS,
      UPDATE_VARIANT_SUCCESS,
      ADD_ITEM_SUCCESS,
      ADD_REDIRECT_ITEM_SUCCESS,
      UPDATE_ITEM_SUCCESS,
      SIMPLE_UPDATE_ITEM_SUCCESS,
      COMPLEX_UPDATE_ITEM_SUCCESS,
      COMPLEX_UPDATE_BREAKDOWN_SUCCESS,
      COMPLEX_UPDATE_ITEM_COST_SUCCESS,
      DELETE_ITEM_SUCCESS,
      ADD_ITEM_COST_SUCCESS,
      UPDATE_ITEM_COST_SUCCESS,
      DELETE_ITEM_COST_SUCCESS,
      ADD_ITEM_BREAKDOWN_SUCCESS,
      DELETE_ITEM_TAX_AMOUNT_SUCCESS,
    ];

    if (typeof action === 'function') {
      return action(store.dispatch, store.getState);
    }
    if (action && typeof action.then === 'function') {
      return action.then(resolved => store.dispatch(resolved));
    }

    const result = next(action);
    const state = store.getState();
    if (selectSplitShipAccessible(state) === false) {
        // Middleware is only for split ship enabled accounts
        return result;
    }

    const splitShip = state.splitShip?.data;
    const breakdowns = state.entities?.breakdowns || {};
    const items = state.entities?.items || {};
    const parsedPath = window.location.pathname.match(/\/(estimate|sales-order|invoice)\/(\d+)/);
    const formNumber = parsedPath?.[2] || 0;
    const orderType = parsedPath?.[1].replace(/-/g, ' ').toUpperCase() || null;
    const currentOrder = formNumber && orderType ? getOrderByNumber(state, { form_number: formNumber, order_type: orderType }) : null;

    if (currentOrder?.order_id && [UPDATE_ORDER_TAX_ID_SUCESS].includes(action.type)) {
        // Always reset split ship on tax changes
        splitShip && store.dispatch(resetSplitShip(currentOrder?.order_id)).then(() => {
            store.dispatch(createLoadOrder(splitShip.parent_id, true));
        });
        prevBreakdowns = JSON.parse(JSON.stringify(breakdowns));
        prevItems = JSON.parse(JSON.stringify(items));

        return result;
    }

    /**
     * This middleware only applies when:
     *   there is a split ship
     *   when the splitship isn't already reset
     *   we're not already in the process of resetting the split ship
     *   there is a current orderId that matches the currently loaded split ship
     *   there are previous breakdowns and items to compare against
     *   when the action triggering this is one that could change the tax amounts
     */
    if (!splitShip ||
      [Status.SPLIT_SHIP_OPEN].includes(splitShip?.status_id) ||
      action.type === `${SPLIT_SHIP_RESET}/pending` ||
      !currentOrder?.order_id ||
      splitShip?.parent_id !== currentOrder?.order_id ||
      Object.keys(prevBreakdowns).length === 0 ||
      Object.keys(prevItems).length === 0 ||
      !whiteListActions.includes(action.type)
    ) {
        prevBreakdowns = JSON.parse(JSON.stringify(breakdowns));
        prevItems = JSON.parse(JSON.stringify(items));
        // No need to check this middleware
        return result;
    }

    const hasFieldChanged = (newObj, prevObj, fields) =>
      fields.some(field => (newObj?.[field] !== prevObj?.[field]));
    const breakdownFields = [
      'quantity', 'unit_price', 'breakdown_margin', 'unit_cost', 'total_cost',
      'size_id', 'color_id', 'product_sku_id',
    ];
    const itemFields = [
      'unit_cost', 'total_margin', 'unit_price', 'total_cost', 'total_subtotal',
      'total_total', 'total_margin_amount', 'store_unit_cost', 'store_margin',
      'store_unit_price', 'store_total_cost', 'currency_id', 'exchange_rate',
      'total_units', 'item_sku',
    ];
    let shouldReset = false;

    // Compare breakdowns and items generically
    const entitiesToCheck = [
      { current: breakdowns, prev: prevBreakdowns, fields: breakdownFields },
      { current: items, prev: prevItems, fields: itemFields }
    ];
    for (const { current, prev, fields } of entitiesToCheck) {
      const keys = Object.keys(current);
      const prevKeys = Object.keys(prev);
      if (keys.length !== prevKeys.length || keys.some(key => !prevKeys.includes(key))) {
        shouldReset = true;
        break;
      }
      for (const uuid of keys) {
        if (hasFieldChanged(current[uuid], prev[uuid], fields)) {
          shouldReset = true;
          break;
        }
      }
      if (shouldReset) break;
    }

    if (shouldReset) {
      store.dispatch(resetSplitShip(splitShip.parent_id)).then(() => {
        store.dispatch(createLoadOrder(splitShip.parent_id, true));
      });
      prevBreakdowns = JSON.parse(JSON.stringify(breakdowns));
      prevItems = JSON.parse(JSON.stringify(items));
    }

    return result;
  };
};

export default splitShipReset;